import { computed, type MaybeRefOrGetter } from "vue";

import ReferralDashboardService, {
  EReferralDirection,
} from "@/api/models/treatmentPlan/referralDashboard";
import type {
  IReferralDashboardFilters,
  IReferralReceiverPracticeFilters,
  TReferralDashboardItem,
  TReferralReceiverPractice,
} from "@/api/models/treatmentPlan/referralDashboard";
import type { TPracticeQrUsage } from "@/api/models/qr/practiceQr/types";
import type { TPatient } from "@/api/models/patient/patient";
import { EReferralDashboardTab, type TFilter } from "@/types/filter/tableFilters";
import type { NameFields } from "@/utils/Formatters";
import { useEntityNameFormatter } from "@/utils/useFormatters";
import { PRACTICE_QR_INTERNAL_TREATMENT_TYPES } from "@/composables/models/qr/usePracticeQr";

import {
  createQueryKeys,
  EFilterType,
  useGenericListQuery,
  useGenericPaginatedQuery,
  type TFilterFieldMap,
  type TOwnedFilterParams,
} from "@/composables/shared/queries";
import {
  ACTIVE_PLAN_STATES,
  ARCHIVED_PLAN_STATES,
  useTreatmentPlanActionableCounts,
} from "@/composables/models/treatmentPlan/useTreatmentPlan.ts";

export const ReferralDashboardQueryKeys = createQueryKeys(
  ReferralDashboardService.queryKey,
  (key) => ({
    dashboard: key<IReferralDashboardFilters>("referral-dashboard"),
    receiverPractices: key<IReferralReceiverPracticeFilters>("receiver-practices"),
  })
);

/**
 * UI filter fields for the referral dashboard.
 *
 * `receiverPractice` is single-value in the UI while the backend filter takes a list, which
 * is what `SELECT_LIST` means. The two appointment ranges are deliberately unbounded on both
 * ends, unlike `dateReferralSent` / `lastUpdate`: a booked consultation is routinely in the
 * future, so capping them at `now` would hide exactly the rows they exist to find.
 *
 * `consultationDate` bounds the *current* appointment only; `appointmentPeriod` matches any
 * appointment the consultation ever had — including replaced and cancelled ones — so a patient
 * who had a March appointment and rebooked for September still matches a March window. The
 * backend takes that one as a single inclusive `"<gte>,<lte>"` parameter requiring both bounds,
 * hence the `encode`: a half-open range it cannot express clears the filter instead.
 */
export const referralDashboardFields = {
  referralStatus: {
    type: EFilterType.SELECT,
    field: "consultation_status",
  },
  receiverPractice: { type: EFilterType.SELECT_LIST, field: "practice_to" },
  consultationDate: { type: EFilterType.DATE_RANGE, field: "current_appointment_start" },
  appointmentPeriod: {
    type: EFilterType.DATE_RANGE,
    field: "appointment_start__range",
    queryKey: "appointment_period",
    encode: ({ start, end }) => (start && end ? `${start},${end}` : undefined),
  },
  dateReferralSent: { type: EFilterType.DATE_RANGE, field: "date_referral_sent" },
  lastUpdate: { type: EFilterType.DATE_RANGE, field: "last_update" },
} satisfies TFilterFieldMap;

export const useReferralDashboard = (
  params: TOwnedFilterParams<IReferralDashboardFilters> = {}
) => {
  const query = useGenericPaginatedQuery<
    TReferralDashboardItem,
    Partial<IReferralDashboardFilters>,
    typeof referralDashboardFields
  >({
    queryKey: ReferralDashboardQueryKeys.dashboard(),
    params: { ...params, fields: referralDashboardFields },
    fetcher: (fetchParams) =>
      ReferralDashboardService.dashboard({}, fetchParams.filters, fetchParams.pageParam),
  });

  return query;
};

export const useReferralReceiverPractices = (enabled: MaybeRefOrGetter<boolean> = true) =>
  useGenericListQuery<TReferralReceiverPractice, Partial<IReferralReceiverPracticeFilters>>({
    queryKey: ReferralDashboardQueryKeys.receiverPractices(),
    params: { enabled },
    fetcher: () => ReferralDashboardService.receiverPractices({}),
  });

export { EReferralDashboardTab } from "@/types/filter/tableFilters";

/**
 * Tab definitions for the referral dashboard.
 *
 * `Actions Needed` reuses `has_actionable_jobs` (same as co-treatment); the two
 * direction tabs use the backend's `referral_type` filter.
 *
 * `state` splits Archived (the finished plans) from Actions Needed and Referrals In (the
 * running ones); omitting it returns both mixed together, so those tabs exclude archived
 * plans explicitly rather than by saying nothing.
 *
 * Referrals Out is the deliberate exception and pins no `state` at all: it reads as an
 * informational overview of everything sent out rather than a task list, so it keeps
 * showing a referral after its plan is completed or failed.
 *
 * Labels are derived from the tab keys by `TableFilterTabs`
 * (`referrals-in` → `models.patient.table.predefinedFilters.referralsIn`).
 */
export const useReferralTabFilters = () => {
  const { data: actionableCounts } = useTreatmentPlanActionableCounts();

  return computed<TFilter<EReferralDashboardTab, IReferralDashboardFilters>[]>(() => [
    {
      tab: EReferralDashboardTab.ACTIONS_NEEDED,
      badgeNumber: actionableCounts.value?.referral,
      badgeColor: "#808080",

      filters: {
        has_actionable_jobs: true,
        referral_type: undefined,
        state: ACTIVE_PLAN_STATES,
      },
    },
    {
      tab: EReferralDashboardTab.REFERRALS_IN,
      filters: {
        has_actionable_jobs: undefined,
        referral_type: EReferralDirection.IN,
        state: ACTIVE_PLAN_STATES,
      },
    },
    {
      tab: EReferralDashboardTab.REFERRALS_OUT,
      filters: {
        has_actionable_jobs: undefined,
        referral_type: EReferralDirection.OUT,
        // Intentionally unset — active and archived flows shown together, see above.
        state: undefined,
      },
    },
    {
      tab: EReferralDashboardTab.ARCHIVED,
      filters: {
        has_actionable_jobs: undefined,
        referral_type: undefined,
        state: ARCHIVED_PLAN_STATES,
      },
    },
  ]);
};

/** What kind of entity a referral came from, which decides how it is labelled. */
export enum EReferralSenderKind {
  TEAM_MEMBER = "team_member",
  EVENT = "event",
  OFFICE = "office",
}

export type TReferralSender = {
  kind: EReferralSenderKind;
  label: string;
  officeName?: string;
  teamMemberName?: string;
  eventName?: string;
  campaignName?: string;
};

export const REFERRAL_SENDER_KIND_LABEL_KEY: Record<EReferralSenderKind, string> = {
  [EReferralSenderKind.TEAM_MEMBER]: "models.patient.table.senderKind.team_member",
  [EReferralSenderKind.EVENT]: "models.patient.table.senderKind.event",
  [EReferralSenderKind.OFFICE]: "generic.fields.practice",
};

export type TReferralSenderSource = {
  referral?: Partial<
    Pick<
      NonNullable<TReferralDashboardItem["referral"]>,
      "practice_from" | "practice_to" | "staff_created_by"
    >
  > | null;
  qr_usage?: TPracticeQrUsage | null;
};

const staffName = (staff: NameFields | null | undefined): string | undefined => {
  if (!staff) {
    return undefined;
  }

  const { formatName } = useEntityNameFormatter();

  return formatName(staff).trim() || undefined;
};

/** Whether a QR usage's `treatment_type` is one of the internal (non-partner) workflows. */
const isInternalQrUsage = (usage: TPracticeQrUsage | null | undefined): boolean => {
  const treatmentType = usage?.practice_qr?.treatment_type;

  return (
    treatmentType == null ||
    (PRACTICE_QR_INTERNAL_TREATMENT_TYPES as readonly string[]).includes(treatmentType)
  );
};
const qrEventName = (usage: TPracticeQrUsage | null | undefined): string | undefined => {
  const event = usage?.practice_qr?.event;

  if (!event || !isInternalQrUsage(usage)) {
    return undefined;
  }

  const name = event.campaignSource?.trim() || event.campaignName?.trim();

  return name || usage?.practice_qr?.description?.trim() || undefined;
};

/**
 * Resolve who sent a referral, per the agreed display rules:
 *
 * 1. QR registered as an internal referral with an **Event** → the event name.
 * 2. QR registered as an internal referral with a **Team Member** → that staff's name.
 * 3. Referral from a cooperating office → the office name, plus whatever QR
 *    tracking data (owning team member, campaign) the referral's QR usage still
 *    carries — surfaced as breakdown even though it didn't win the label.
 *
 * Falls back to the office/creating-staff data on the referral itself when the
 * plan has no QR usage.
 *
 * The QR's `owner_staff` is who the QR was registered *to*, so it identifies a Team
 * Member sender directly (case 2); the referral's `staff_created_by` is only whoever
 * clicked send, so it stays a fallback.
 */
export const resolveReferralSender = (item: TReferralSenderSource): TReferralSender | null => {
  const latestUsage = item.qr_usage;

  const eventName = qrEventName(latestUsage);
  const practiceFrom = item.referral?.practice_from;
  const qrOwner = latestUsage?.practice_qr?.owner_staff;
  const teamMemberName =
    staffName(typeof qrOwner === "string" ? undefined : qrOwner) ??
    staffName(item.referral?.staff_created_by);

  if (eventName) {
    const campaignName = latestUsage?.practice_qr?.event?.campaignName?.trim();

    return {
      kind: EReferralSenderKind.EVENT,
      label: eventName,
      officeName: practiceFrom?.name,
      teamMemberName,
      // Only surfaced when it lost to `campaignSource` for the label — otherwise
      // it would just repeat what's already shown up top.
      campaignName: campaignName && campaignName !== eventName ? campaignName : undefined,
    };
  }

  if (practiceFrom?.name) {
    const qrEvent = latestUsage?.practice_qr?.event;
    const qrTeamMemberName = staffName(typeof qrOwner === "string" ? undefined : qrOwner);

    return {
      kind: EReferralSenderKind.OFFICE,
      label: practiceFrom.name,
      teamMemberName: qrTeamMemberName,
      eventName: qrEvent?.campaignSource,
      campaignName: qrEvent?.campaignName,
    };
  }

  if (teamMemberName) {
    return { kind: EReferralSenderKind.TEAM_MEMBER, label: teamMemberName };
  }

  return null;
};

/** The receiving side of a referral — always an office. */
export const resolveReferralReceiver = (item: TReferralSenderSource): string | null =>
  item.referral?.practice_to?.name ?? null;

export const referralStatusPatient = (item: TReferralDashboardItem): TPatient =>
  ({
    ...item.patient,

    actual_treatment_plans: item.referral
      ? [
          {
            id: item.id,
            referral: {
              id: item.referral.id,
              created_at: item.referral.created_at,
              status: item.referral.status,
              status_changed_at: item.referral.status_changed_at,
            },
          },
        ]
      : [],
  }) as unknown as TPatient;
